/**
 * Plain on-change input shaping, replacing the legacy jQuery `formatter()`
 * patterns the Sprockets forms used. Pure string functions with no React or
 * component dependency, so a login-screen bundle can import one without
 * pulling in a whole form module.
 */

const digitsOnly = (value: string): string => value.replace(/\D/g, '')

/** `{{99999}}` and friends: digits only, capped. */
export const maskDigits = (value: string, maxLength: number): string =>
  digitsOnly(value).slice(0, maxLength)

/** `({{999}}) {{999}}-{{9999}}` */
export const maskPhone = (value: string): string => {
  const digits = maskDigits(value, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * The digits-only state behind a US phone field.
 *
 * Phone inputs hold digits and render them through `maskPhone`, so the field can
 * only ever contain the digits of a US number: letters and separators are
 * dropped as they are typed, and an eleventh digit can't be entered at all. A US
 * number never starts with 1, so a leading 1 on eleven or more digits is the +1
 * country code of a paste or a tel autofill, and is dropped rather than kept.
 */
export const maskPhoneDigits = (value: string): string => {
  const digits = digitsOnly(value)
  const national = digits.length >= 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return national.slice(0, 10)
}

/** True once a `maskPhoneDigits` value is a complete 10-digit number. */
export const isCompletePhone = (value: string): boolean => maskPhoneDigits(value).length === 10
